import { createTheme, ThemeOptions, responsiveFontSizes } from '@mui/material/styles';

// Define responsive breakpoints
const breakpoints = {
  values: {
    xs: 0,
    sm: 600,
    md: 900,
    lg: 1200,
    xl: 1536,
  },
};

// Define spacing system
const spacing = (factor: number) => `${0.25 * factor}rem`;

// Define typography scale
const typography = {
  fontFamily: [
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Roboto',
    '"Helvetica Neue"',
    'Arial',
    'sans-serif',
    '"Apple Color Emoji"',
    '"Segoe UI Emoji"',
    '"Segoe UI Symbol"',
  ].join(','),
  h1: {
    fontSize: '2.5rem',
    fontWeight: 600,
    lineHeight: 1.2,
    [`@media (max-width:${breakpoints.values.md}px)`]: {
      fontSize: '2rem',
    },
    [`@media (max-width:${breakpoints.values.sm}px)`]: {
      fontSize: '1.75rem',
    },
  },
  h2: {
    fontSize: '2rem',
    fontWeight: 600,
    lineHeight: 1.3,
    [`@media (max-width:${breakpoints.values.md}px)`]: {
      fontSize: '1.75rem',
    },
    [`@media (max-width:${breakpoints.values.sm}px)`]: {
      fontSize: '1.5rem',
    },
  },
  h3: {
    fontSize: '1.75rem',
    fontWeight: 600,
    lineHeight: 1.4,
    [`@media (max-width:${breakpoints.values.md}px)`]: {
      fontSize: '1.5rem',
    },
    [`@media (max-width:${breakpoints.values.sm}px)`]: {
      fontSize: '1.25rem',
    },
  },
  h4: {
    fontSize: '1.5rem',
    fontWeight: 600,
    lineHeight: 1.4,
    [`@media (max-width:${breakpoints.values.md}px)`]: {
      fontSize: '1.25rem',
    },
    [`@media (max-width:${breakpoints.values.sm}px)`]: {
      fontSize: '1.125rem',
    },
  },
  h5: {
    fontSize: '1.25rem',
    fontWeight: 600,
    lineHeight: 1.5,
    [`@media (max-width:${breakpoints.values.sm}px)`]: {
      fontSize: '1.125rem',
    },
  },
  h6: {
    fontSize: '1.125rem',
    fontWeight: 600,
    lineHeight: 1.5,
    [`@media (max-width:${breakpoints.values.sm}px)`]: {
      fontSize: '1rem',
    },
  },
  body1: {
    fontSize: '1rem',
    lineHeight: 1.6,
    [`@media (max-width:${breakpoints.values.sm}px)`]: {
      fontSize: '0.875rem',
    },
  },
  body2: {
    fontSize: '0.875rem',
    lineHeight: 1.6,
    [`@media (max-width:${breakpoints.values.sm}px)`]: {
      fontSize: '0.8125rem',
    },
  },
  button: {
    fontSize: '0.875rem',
    fontWeight: 500,
    textTransform: 'none' as const,
    [`@media (max-width:${breakpoints.values.sm}px)`]: {
      fontSize: '0.8125rem',
    },
  },
};

// Define color palette
const palette = {
  primary: {
    main: '#1976d2',
    light: '#42a5f5',
    dark: '#1565c0',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#dc004e',
    light: '#ff5983',
    dark: '#9a0036',
    contrastText: '#ffffff',
  },
  error: {
    main: '#f44336',
    light: '#e57373',
    dark: '#d32f2f',
    contrastText: '#ffffff',
  },
  warning: {
    main: '#ff9800',
    light: '#ffb74d',
    dark: '#f57c00',
    contrastText: '#ffffff',
  },
  info: {
    main: '#2196f3',
    light: '#64b5f6',
    dark: '#1976d2',
    contrastText: '#ffffff',
  },
  success: {
    main: '#4caf50',
    light: '#81c784',
    dark: '#388e3c',
    contrastText: '#ffffff',
  },
  background: {
    default: '#fafafa',
    paper: '#ffffff',
  },
  text: {
    primary: 'rgba(0, 0, 0, 0.87)',
    secondary: 'rgba(0, 0, 0, 0.6)',
    disabled: 'rgba(0, 0, 0, 0.38)',
  },
  divider: 'rgba(0, 0, 0, 0.12)',
};

// Define component overrides for responsive design
const components: ThemeOptions['components'] = {
  MuiContainer: {
    styleOverrides: {
      root: {
        paddingLeft: spacing(2),
        paddingRight: spacing(2),
        [`@media (max-width:${breakpoints.values.sm}px)`]: {
          paddingLeft: spacing(1.5),
          paddingRight: spacing(1.5),
        },
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: '12px',
        [`@media (max-width:${breakpoints.values.sm}px)`]: {
          borderRadius: '8px',
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        [`@media (max-width:${breakpoints.values.sm}px)`]: {
          borderRadius: '8px',
          boxShadow: '0 1px 4px rgba(0, 0, 0, 0.1)',
        },
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: '8px',
        padding: `${spacing(1)} ${spacing(2)}`,
        [`@media (max-width:${breakpoints.values.sm}px)`]: {
          borderRadius: '6px',
          padding: `${spacing(0.75)} ${spacing(1.5)}`,
          minHeight: '36px',
        },
      },
      sizeLarge: {
        padding: `${spacing(1.5)} ${spacing(3)}`,
        [`@media (max-width:${breakpoints.values.sm}px)`]: {
          padding: `${spacing(1.25)} ${spacing(2.5)}`,
          minHeight: '44px',
        },
      },
      sizeSmall: {
        padding: `${spacing(0.5)} ${spacing(1.5)}`,
        [`@media (max-width:${breakpoints.values.sm}px)`]: {
          padding: `${spacing(0.5)} ${spacing(1)}`,
          minHeight: '32px',
        },
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: '8px',
          [`@media (max-width:${breakpoints.values.sm}px)`]: {
            borderRadius: '6px',
          },
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: '16px',
        [`@media (max-width:${breakpoints.values.sm}px)`]: {
          borderRadius: '12px',
          fontSize: '0.75rem',
          height: '24px',
        },
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: '16px',
        [`@media (max-width:${breakpoints.values.sm}px)`]: {
          borderRadius: '12px',
          margin: spacing(2),
          maxHeight: 'calc(100% - 32px)',
        },
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        [`@media (max-width:${breakpoints.values.md}px)`]: {
          width: '280px',
        },
        [`@media (max-width:${breakpoints.values.sm}px)`]: {
          width: '260px',
        },
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      },
    },
  },
  MuiTableContainer: {
    styleOverrides: {
      root: {
        borderRadius: '12px',
        [`@media (max-width:${breakpoints.values.sm}px)`]: {
          borderRadius: '8px',
        },
      },
    },
  },
  MuiTable: {
    styleOverrides: {
      root: {
        '& .MuiTableCell-head': {
          backgroundColor: '#f5f5f5',
          fontWeight: 600,
          [`@media (max-width:${breakpoints.values.sm}px)`]: {
            fontSize: '0.8125rem',
            padding: spacing(1),
          },
        },
        '& .MuiTableCell-body': {
          [`@media (max-width:${breakpoints.values.sm}px)`]: {
            fontSize: '0.8125rem',
            padding: spacing(1),
          },
        },
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: {
        [`@media (max-width:${breakpoints.values.sm}px)`]: {
          minHeight: '40px',
        },
      },
      indicator: {
        height: '3px',
        [`@media (max-width:${breakpoints.values.sm}px)`]: {
          height: '2px',
        },
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none' as const,
        minHeight: '48px',
        [`@media (max-width:${breakpoints.values.sm}px)`]: {
          minHeight: '40px',
          fontSize: '0.875rem',
          padding: `${spacing(0.5)} ${spacing(1)}`,
        },
      },
    },
  },
  MuiFormControl: {
    styleOverrides: {
      root: {
        [`@media (max-width:${breakpoints.values.sm}px)`]: {
          '& .MuiInputLabel-root': {
            fontSize: '0.875rem',
          },
          '& .MuiSelect-select': {
            fontSize: '0.875rem',
          },
        },
      },
    },
  },
};

// Create the base theme
const baseTheme = createTheme({
  breakpoints,
  spacing: 8, // This will be used by the spacing function
  palette,
  typography,
  components,
  shape: {
    borderRadius: 8,
  },
});

// Apply responsive font sizes
const responsiveTheme = responsiveFontSizes(baseTheme);

export default responsiveTheme;
